import React, { useState, useEffect } from 'react';
import { Layout, Menu, theme, Typography, Space, Avatar, Button, Card, Row, Col, Input, Select, Table, Pagination, Dropdown, message, Spin, Modal, Tooltip, Tag, Drawer, Empty, Descriptions, DatePicker, Statistic} from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import UserProfile from './UserProfile';
import {
  DashboardOutlined, UserOutlined, StarOutlined, RiseOutlined, SettingOutlined,
  ExclamationCircleOutlined,
  MenuOutlined, SearchOutlined, UserAddOutlined,
  SortAscendingOutlined, EditOutlined,
  GiftOutlined, TeamOutlined, GlobalOutlined, DeleteOutlined,
  MailOutlined, EnvironmentOutlined, CalculatorOutlined, CreditCardOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import InviteDonorModal from './InviteDonorModal';
import EditDonorModal from './EditDonorModal';
import { donorAPI, beneficiaryAPI } from '../services/api';
import { addNotification } from '../services/notifications';
import '../styles/sidebar-standard.css';
import '../styles/menu-hover-overrides.css';
import './Donors.css';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const Donors: React.FC = () => {
  const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  /**
   * Show every matching donor in one scroll instead of paged.
   *
   * Filtering already runs over the whole donor set client-side, so this only
   * stops the slice below — the filters and the "N Donors Found" count were
   * always describing the full result, and paging just hid most of it.
   */
  const [showAllDonors, setShowAllDonors] = useState(false);
  const [pageSize, setPageSize] = useState(12);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('30-days');
  const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingDonor, setEditingDonor] = useState<any>(null);
  const [isDeleteUserModalVisible, setIsDeleteUserModalVisible] = useState(false);
  const [deletingUser, setDeletingUser] = useState<any>(null);
  const [donorsData, setDonorsData] = useState<any[]>([]);
  const [allDonorsData, setAllDonorsData] = useState<any[]>([]);
  const [filteredDonorsData, setFilteredDonorsData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalDonors, setTotalDonors] = useState(0);
  const [resendingInvitation, setResendingInvitation] = useState<number | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<string | undefined>(undefined);
  /**
   * Filter by the calendar month a donor actually paid in.
   *
   * Replaces a rolling-window dropdown (1 week / 1 month / 3 months …) that
   * filtered on last_donation_date. That answers a different question than it
   * appears to: last_donation_date is a single date, so picking a past month
   * found donors whose LAST EVER payment fell in it — people who lapsed
   * afterwards — and missed anyone who also paid later. Someone who gave in
   * both August and September was invisible in August.
   *
   * The month is resolved server-side from `transactions`, using the same
   * predicates as the Beneficiary Payouts report, so the two screens agree.
   * Verified: Sept 2/2, Aug 10/10, Jul 7/7 donations against that report.
   */
  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(null);
  const [monthDonorIds, setMonthDonorIds] = useState<Set<number> | null>(null);
  const [monthLoading, setMonthLoading] = useState(false);
  const [selectedUserStatus, setSelectedUserStatus] = useState<string | undefined>(undefined);
  const [selectedCityState, setSelectedCityState] = useState<string | undefined>(undefined);
  const [selectedCoworking, setSelectedCoworking] = useState<string | undefined>(undefined);
  // Stripe billing drawer — what the donor is actually charged, read from
  // Stripe rather than our tables, because the two can disagree.
  const [billingDonor, setBillingDonor] = useState<any | null>(null);
  const [billingData, setBillingData] = useState<any | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [selectedSubscriptionStatus, setSelectedSubscriptionStatus] = useState<string | undefined>(undefined);
  // Resolve the selected month to the set of donors who paid in it. Server-side
  // because the donor row only carries last_donation_date, which cannot answer
  // "did they pay during this month".
  useEffect(() => {
    if (!selectedMonth) {
      setMonthDonorIds(null);
      return;
    }
    let cancelled = false;
    const month = selectedMonth.format('YYYY-MM');
    setMonthLoading(true);
    donorAPI
      .getDonatedIn(month)
      .then((res: any) => {
        if (cancelled) return;
        const ids: number[] = res?.success ? res.data?.user_ids || [] : [];
        setMonthDonorIds(new Set(ids.map(Number)));
      })
      .catch(() => {
        // An empty set would hide every donor and read as "nobody gave".
        // Null clears the filter instead, which is the honest failure.
        if (!cancelled) setMonthDonorIds(null);
      })
      .finally(() => {
        if (!cancelled) setMonthLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedMonth]);

  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | null>(null);
  const [beneficiariesList, setBeneficiariesList] = useState<any[]>([]);
  const [citiesList, setCitiesList] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // Load donors from API
  const loadDonors = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const collected: any[] = [];
      let page = 1;
      const limit = Math.max(pageSize, 200);
      let total = 0;
      let response: any = null;

      do {
        response = await donorAPI.getDonors(page, limit);
        if (response?.success && Array.isArray(response.data)) {
          collected.push(...response.data);
        }
        total = response?.pagination?.total || collected.length;
        page += 1;
      } while (collected.length < total);
      
      if (response?.success) {
        // Transform API data to match our table structure
        const transformedData = collected.map((donor: any) => ({
          key: donor.id.toString(),
          id: donor.id, // Store original ID for API calls
          name: donor.name || 'Unknown',
          email: donor.email || 'N/A',
          contact: donor.phone || 'N/A',
          beneficiary: donor.beneficiary_name || 'N/A',
          coworking: donor.coworking ? 'Yes' : 'No',
          // 'standard' | 'coworking' | 'team'. The API now returns invite_type;
          // the coworking boolean is kept as a fallback for rows written before
          // it did.
          membership: donor.invite_type || (donor.coworking ? 'coworking' : 'standard'),
          donation: donor.total_donations ? `$${donor.total_donations}` : '$0',
          oneTime: donor.one_time_donation ? `$${donor.one_time_donation}` : '$0',
          lastDonated: donor.last_donation_date ? new Date(donor.last_donation_date).toLocaleDateString() : 'Never',
          subscriptionStatus: donor.subscription_status || 'no_subscription',
          cityState: (() => {
            if (!donor.address) return 'N/A';
            const city = donor.address.city || '';
            const state = donor.address.state || '';
            if (city && state) {
              return `${city}, ${state}`;
            } else if (city) {
              return city;
            } else if (state) {
              return state;
            }
            return 'N/A';
          })(),
          // Store location data for potential future use
          latitude: donor.address?.latitude || donor.latitude || null,
          longitude: donor.address?.longitude || donor.longitude || null,
          locationPermissionGranted: donor.location_permission_granted || donor.address?.location_permission_granted || false,
          active: donor.is_active || false,
          enabled: donor.is_enabled || false,
          avatar: donor.name ? donor.name.split(' ').map((n: string) => n[0]).join('').toUpperCase() : 'D',
          // Store original data for editing
          originalData: donor
        }));
        
        setDonorsData(transformedData);
        setAllDonorsData(transformedData);
        setFilteredDonorsData(transformedData);
        setTotalDonors(transformedData.length);
        
        // Extract unique cities/states for filter
        const cities = Array.from(new Set(
          transformedData
            .map((d: any) => d.cityState)
            .filter((cs: string) => cs && cs !== 'N/A')
        )).sort() as string[];
        setCitiesList(cities);
        
      } else {
        setError('Failed to load donors');
        setDonorsData([]);
        setAllDonorsData([]);
        setTotalDonors(0);
      }
    } catch (error: any) {
      console.error('Error loading donors:', error);
      
      // Check if it's a 404 error (endpoint not ready)
      if (error.message && error.message.includes('404')) {
        setError('Backend endpoint is being prepared. Use "Invite Donor" button to add donors.');
        setDonorsData([]);
        setAllDonorsData([]);
        setTotalDonors(0);
      } else {
        setError('Failed to load donors');
        setDonorsData([]);
        setAllDonorsData([]);
        setTotalDonors(0);
      }
    } finally {
      setLoading(false);
    }
  };


  // Load beneficiaries for filter
  const loadBeneficiaries = async () => {
    try {
      const response = await beneficiaryAPI.getBeneficiaries(1, 1000);
      if (response.success && response.data) {
        setBeneficiariesList(response.data);
      }
    } catch (error) {
      console.error('Error loading beneficiaries:', error);
    }
  };

  // Load data on component mount and when page changes
  useEffect(() => {
    loadDonors();
    loadBeneficiaries();
  }, [pageSize]);

  // Apply filters when filter values change
  useEffect(() => {
    applyFilters();
  }, [allDonorsData, searchText, selectedBeneficiary, selectedMonth, monthDonorIds, selectedUserStatus, selectedCityState, selectedCoworking, selectedSubscriptionStatus]);

  const applyFilters = () => {
    let filtered = [...allDonorsData];

    // Search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((donor: any) =>
        donor.name?.toLowerCase().includes(searchLower) ||
        donor.email?.toLowerCase().includes(searchLower) ||
        donor.contact?.toLowerCase().includes(searchLower)
      );
    }

    // Beneficiary filter
    if (selectedBeneficiary) {
      const selectedBeneficiaryName = beneficiariesList.find((b: any) => b.id?.toString() === selectedBeneficiary)?.name;
      filtered = filtered.filter((donor: any) => 
        donor.beneficiary === selectedBeneficiaryName || 
        donor.originalData?.beneficiary_id?.toString() === selectedBeneficiary ||
        donor.originalData?.beneficiary_name === selectedBeneficiaryName
      );
    }

    // Donated-in-month filter. monthDonorIds is the authoritative set from the
    // server; null means no month is selected, an empty set means the month had
    // no donations at all (which must show nothing, not everything).
    if (selectedMonth && monthDonorIds) {
      filtered = filtered.filter((donor: any) => {
        const id = Number(donor.id ?? donor.originalData?.id);
        return Number.isFinite(id) && monthDonorIds.has(id);
      });
    }

    // User status filter
    if (selectedUserStatus) {
      if (selectedUserStatus === 'active') {
        filtered = filtered.filter((donor: any) => donor.active === true);
      } else if (selectedUserStatus === 'inactive') {
        filtered = filtered.filter((donor: any) => donor.active === false);
      }
    }

    // City/State filter
    if (selectedCityState) {
      filtered = filtered.filter((donor: any) => donor.cityState === selectedCityState);
    }

    // Coworking filter
    if (selectedCoworking) {
      // Values are membership types now. 'yes'/'no' are still handled so a
      // bookmarked or cached filter value doesn't silently match nothing.
      if (selectedCoworking === 'yes') {
        filtered = filtered.filter((donor: any) => donor.membership === 'coworking');
      } else if (selectedCoworking === 'no') {
        filtered = filtered.filter((donor: any) => donor.membership !== 'coworking');
      } else {
        filtered = filtered.filter(
          (donor: any) => (donor.membership || 'standard') === selectedCoworking,
        );
      }
    }

    // Subscription status filter (Active / Past Due / Cancelled / etc.)
    if (selectedSubscriptionStatus) {
      filtered = filtered.filter((donor: any) => {
        const s = (donor.subscriptionStatus || 'no_subscription').toLowerCase();
        if (selectedSubscriptionStatus === 'active') {
          return s === 'active' || s === 'trialing';
        }
        if (selectedSubscriptionStatus === 'past_due') {
          return s === 'past_due' || s === 'unpaid';
        }
        if (selectedSubscriptionStatus === 'cancelled') {
          return s === 'cancelled' || s === 'canceled';
        }
        if (selectedSubscriptionStatus === 'no_subscription') {
          return s === 'no_subscription';
        }
        return s === selectedSubscriptionStatus;
      });
    }

    setFilteredDonorsData(filtered);
  };

  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchText, selectedBeneficiary, selectedMonth, monthDonorIds, selectedUserStatus, selectedCityState, selectedCoworking, selectedSubscriptionStatus]);

  const sortedDonors = [...filteredDonorsData].sort((a, b) => {
    if (!sortField || !sortOrder) return 0;
    const valueA = (a as any)[sortField];
    const valueB = (b as any)[sortField];
    const normalizedA = valueA ? valueA.toString().toLowerCase() : '';
    const normalizedB = valueB ? valueB.toString().toLowerCase() : '';
    const comparison = normalizedA.localeCompare(normalizedB);
    return sortOrder === 'ascend' ? comparison : -comparison;
  });

  const paginatedDonors = showAllDonors
    ? sortedDonors
    : sortedDonors.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleToggleChange = async (key: string, field: 'active' | 'enabled') => {
    const donorId = parseInt(key, 10);
    if (isNaN(donorId)) return;
    const donor = allDonorsData.find((d) => d.key === key);
    if (!donor) return;
    const nextVal = !donor[field];
    const payload = field === 'active' ? { is_active: nextVal } : { is_enabled: nextVal };
    try {
      const response = await donorAPI.updateDonor(donorId, payload);
      if (response?.success !== false) {
        const update = (prev: any[]) =>
          prev.map((d) => (d.key === key ? { ...d, [field]: nextVal } : d));
        setDonorsData(update);
        setAllDonorsData(update);
        setFilteredDonorsData(update);
        message.success('Donor updated');
        loadDonors(); // Refresh from server
      } else {
        message.error('Failed to update donor');
      }
    } catch (err) {
      console.error('Error updating donor:', err);
      message.error('Failed to update donor');
    }
  };

  const handleTimeFilterChange = (key: string) => {
    setSelectedTimeFilter(key);
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === 'dashboard') {
      navigate('/dashboard');
    } else if (key === 'donors') {
      navigate('/donors');
    } else if (key === 'vendor') {
      navigate('/vendor');
    } else if (key === 'beneficiaries') {
      navigate('/beneficiaries');
    } else if (key === 'discounts') {
      navigate('/discounts');
    } else if (key === 'pending-approvals') {
      navigate('/pending-approvals');
    } else if (key === 'invitations') {
      navigate('/invitations');
    } else if (key === 'referral-analytics') {
      navigate('/referral-analytics');
    } else if (key === 'geographic-analytics') {
      navigate('/geographic-analytics');
    } else if (key === 'reporting') {
      navigate('/reporting');
    } else if (key === 'settings') {
      navigate('/settings');
    }
  };

  const timeFilterMenu = [
    {
      key: '7-days',
      label: 'Last 7 Days',
      onClick: () => handleTimeFilterChange('7-days')
    },
    {
      key: '30-days',
      label: 'Last 30 Days',
      onClick: () => handleTimeFilterChange('30-days')
    },
    {
      key: '90-days',
      label: 'Last 90 Days',
      onClick: () => handleTimeFilterChange('90-days')
    },
    {
      key: '1-year',
      label: 'Last 1 Year',
      onClick: () => handleTimeFilterChange('1-year')
    },
    {
      key: 'all-time',
      label: 'All Time',
      onClick: () => handleTimeFilterChange('all-time')
    }
  ];

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      title: 'Dashboard Overview'
    },
    {
      key: 'donors',
      icon: <UserOutlined />,
      label: 'Donors',
      title: 'Donor Management'
    },
    {
      key: 'beneficiaries',
      icon: <StarOutlined />,
      label: 'Beneficiaries',
      title: 'Beneficiary Management'
    },
    {
      key: 'vendor',
      icon: <RiseOutlined />,
      label: 'Vendor',
      title: 'Vendor Management'
    },
    {
      key: 'discounts',
      icon: <GiftOutlined />,
      label: 'Discounts',
      title: 'Discount Management'
    },
    {
      key: 'pending-approvals',
      icon: <ExclamationCircleOutlined />,
      label: 'Pending Approvals',
      title: 'Pending Approvals'
    },
    {
      key: 'invitations',
      icon: <MailOutlined />,
      label: 'Invitations',
      title: 'Beneficiary & Vendor Invitations'
    },
    {
      key: 'referral-analytics',
      icon: <TeamOutlined />,
      label: 'Referral Analytics',
      title: 'Referral Analytics & Tracking'
    },
    {
      key: 'geographic-analytics',
      icon: <GlobalOutlined />,
      label: 'Geographic Analytics',
      title: 'Geographic Analytics & Insights'
    },
    {
      key: 'reporting',
      icon: <CalculatorOutlined />,
      label: 'Reporting',
      title: 'Payouts & Financial Reporting'
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      title: 'System Settings & Configuration'
    },
  ];

  const columns = [
    {
      title: (
        <div className="sortable-header">
          Donor name
          <SortAscendingOutlined className="sort-icon" />
        </div>
      ),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortField === 'name' ? sortOrder : null,
      render: (text: string, record: any) => (
        <Space
          onClick={(e) => {
            e.stopPropagation();
            handleEditDonor(record);
          }}
          className="donor-name-cell"
          style={{ cursor: 'pointer', width: '100%' }}
        >
          <Avatar size={32} style={{ backgroundColor: '#DB8633' }}>
            {record.avatar}
          </Avatar>
          <Text strong className="donor-name-text" style={{ cursor: 'pointer' }}>{text}</Text>
        </Space>
      ),
      fixed: 'left' as const,
      width: 260,
    },
    {
      title: 'Emails',
      dataIndex: 'email',
      key: 'email',
      render: (text: string) => <Text type="secondary">{text}</Text>,
      width: 250,
    },
    {
      title: 'Contact number',
      dataIndex: 'contact',
      key: 'contact',
      render: (text: string) => <Text type="secondary">{text}</Text>,
      width: 180,
    },
    {
      title: 'Selected beneficiary name',
      dataIndex: 'beneficiary',
      key: 'beneficiary',
      render: (text: string) => <Text type="secondary">{text}</Text>,
      width: 280,
    },
    {
      title: (
        <div className="sortable-header">
          Membership
          <SortAscendingOutlined className="sort-icon" />
        </div>
      ),
      dataIndex: 'membership',
      key: 'membership',
      render: (value: string) => {
        const type = (value || 'standard').toLowerCase();
        const label =
          type === 'team' ? 'Team' : type === 'coworking' ? 'Coworking' : 'General';
        // Team is called out in orange because those accounts are comped and
        // excluded from donation totals — worth spotting at a glance.
        const color =
          type === 'team' ? 'orange' : type === 'coworking' ? 'blue' : 'default';
        return <Tag color={color}>{label}</Tag>;
      },
      width: 150,
    },
    {
      title: (
        <div className="sortable-header">
          Donation
          <SortAscendingOutlined className="sort-icon" />
        </div>
      ),
      dataIndex: 'donation',
      key: 'donation',
      render: (text: string) => <Text strong style={{ color: '#DB8633' }}>{text}</Text>,
      width: 140,
    },
    {
      title: (
        <div className="sortable-header">
          One time gifts
          <SortAscendingOutlined className="sort-icon" />
        </div>
      ),
      dataIndex: 'oneTime',
      key: 'oneTime',
      render: (text: string) => <Text strong style={{ color: '#DB8633' }}>{text}</Text>,
      width: 160,
    },
    {
      title: (
        <div className="sortable-header">
          Last donated date
          <SortAscendingOutlined className="sort-icon" />
        </div>
      ),
      dataIndex: 'lastDonated',
      key: 'lastDonated',
      render: (text: string) => <Text type="secondary">{text}</Text>,
      width: 180,
    },
    {
      title: 'Subscription',
      dataIndex: 'subscriptionStatus',
      key: 'subscriptionStatus',
      width: 130,
      render: (status: string) => {
        const s = (status || 'no_subscription').toLowerCase();
        // Map raw Stripe / DB statuses to a colored tag for fast scanning.
        const map: Record<string, { color: string; label: string }> = {
          active: { color: 'green', label: 'Active' },
          trialing: { color: 'green', label: 'Trialing' },
          past_due: { color: 'orange', label: 'Past Due' },
          unpaid: { color: 'orange', label: 'Unpaid' },
          paused: { color: 'gold', label: 'Paused' },
          cancelled: { color: 'red', label: 'Cancelled' },
          canceled: { color: 'red', label: 'Cancelled' },
          incomplete: { color: 'default', label: 'Incomplete' },
          pending: { color: 'default', label: 'Pending' },
          no_subscription: { color: 'default', label: 'No Sub' },
        };
        const cfg = map[s] || { color: 'default', label: status || '—' };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: (
        <div className="sortable-header">
          City, State
          <SortAscendingOutlined className="sort-icon" />
        </div>
      ),
      dataIndex: 'cityState',
      key: 'cityState',
      render: (text: string, record: any) => (
        <Space>
          <Text type="secondary">{text}</Text>
          {record.locationPermissionGranted && (
            <Tooltip title={`Location permission granted. Coordinates: ${record.latitude ? record.latitude.toFixed(4) : 'N/A'}, ${record.longitude ? record.longitude.toFixed(4) : 'N/A'}`}>
              <EnvironmentOutlined style={{ color: '#52c41a', fontSize: '14px' }} />
            </Tooltip>
          )}
        </Space>
      ),
      width: 180,
    },
    {
      // Renamed from "Active/De-active", which read as a giving status and is
      // not one: this is users.is_active, a manual admin switch that defaults
      // to true at signup and is never derived from donations. On 2026-09-04,
      // 11 of 26 donors were "Active" here while having given $0 and holding
      // no subscription. The Subscription column beside it is the real answer.
      title: (
        <Space size={4}>
          Account Enabled
          <Tooltip
            title={
              'Whether this account can sign in and use the app — a manual switch, ' +
              'on by default at signup. It says nothing about whether they are ' +
              'giving; see the Subscription column for that.'
            }
          >
            <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean, record: any) => (
        <div className="toggle-switch">
          <div 
            className={`toggle ${active ? 'active' : 'inactive'}`} 
            onClick={(e) => {
              e.stopPropagation();
              handleToggleChange(record.key, 'active');
            }}
          >
            <div className="toggle-slider"></div>
          </div>
        </div>
      ),
      width: 160,
    },
    {
      title: 'Enable/Disable',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled: boolean, record: any) => (
        <div className="toggle-switch">
          <div 
            className={`toggle ${enabled ? 'active' : 'inactive'}`} 
            onClick={(e) => {
              e.stopPropagation();
              handleToggleChange(record.key, 'enabled');
            }}
          >
            <div className="toggle-slider"></div>
          </div>
        </div>
      ),
      width: 160,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      width: 220,
      align: 'center' as const,
      render: (text: string, record: any, index: number) => {
        if (!record) {
          return <div style={{ color: 'red', padding: '10px' }}>❌ No record</div>;
        }
        
        const isResending = resendingInvitation === record.id;
        
        return (
          <div 
            id={`actions-${record?.key || record?.id}`}
            className="actions-column-container"
            style={{ 
              display: 'flex', 
              gap: '6px', 
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              maxWidth: '100%',
              padding: '4px 0',
              boxSizing: 'border-box',
              margin: '0 auto'
            }}
          >
            <Button 
              size="middle"
              icon={<EditOutlined />}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                handleEditDonor(record);
              }}
              className="edit-donor-button"
              title="Edit Donor"
              style={{
                backgroundColor: '#fff7e6',
                borderColor: '#DB8633',
                color: '#DB8633',
                fontWeight: 600,
                width: '32px',
                height: '32px',
                padding: 0,
                minWidth: '32px',
                maxWidth: '32px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                borderWidth: '1.5px',
                borderStyle: 'solid',
                flexShrink: 0
              }}
            />
            <Button
              size="middle"
              icon={<CreditCardOutlined />}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                openBilling(record);
              }}
              className="donor-billing-button"
              title="Billing history from Stripe"
              style={{
                backgroundColor: '#f6ffed',
                borderColor: '#52c41a',
                color: '#52c41a',
                fontWeight: 600,
                width: '32px',
                height: '32px',
                padding: 0,
                minWidth: '32px',
                maxWidth: '32px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                borderWidth: '1.5px',
                borderStyle: 'solid',
                flexShrink: 0,
              }}
            />
            <Button 
              size="middle"
              icon={<MailOutlined />}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                handleResendInvitation(record);
              }}
              className="resend-invitation-button"
              title="Resend Invitation Email"
              loading={isResending}
              disabled={isResending}
              style={{
                backgroundColor: '#e6f7ff',
                borderColor: '#1890ff',
                color: '#1890ff',
                width: '32px',
                height: '32px',
                padding: 0,
                minWidth: '32px',
                maxWidth: '32px',
                fontSize: '14px',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isResending ? 'not-allowed' : 'pointer',
                borderWidth: '1.5px',
                borderStyle: 'solid',
                flexShrink: 0,
                opacity: isResending ? 0.6 : 1
              }}
            />
            <Button 
              size="middle"
              icon={<DeleteOutlined />}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                handleDeleteUser(record);
              }}
              className="delete-donor-button"
              title="Delete Donor"
              style={{ 
                backgroundColor: '#fff1f0',
                borderColor: '#ff4d4f',
                color: '#ff4d4f',
                width: '32px',
                height: '32px',
                padding: 0,
                minWidth: '32px',
                maxWidth: '32px',
                fontSize: '14px',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                borderWidth: '1.5px',
                borderStyle: 'solid',
                flexShrink: 0
              }}
            />
          </div>
        );
      },
    },
  ];

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size) setPageSize(size);
  };

  const handleInviteDonor = async (values: any): Promise<boolean> => {
    try {
      // 'membership' replaced the old Coworking yes/no. Fall back to the legacy
      // field so an older cached bundle of the form still submits correctly.
      const membership: string =
        values.membership ||
        (values.coworking === 'Yes' || values.coworking === true
          ? 'coworking'
          : 'standard');
      const isCoworking = membership === 'coworking';
      const isTeam = membership === 'team';
      // Only coworking has a sponsor paying per seat. Team is comped, so any
      // amount left in the form field is ignored rather than stored as a
      // pledge nobody is billed for.
      const sponsorAmount = isTeam
        ? 0
        : values.sponsorAmount !== undefined && values.sponsorAmount !== null && values.sponsorAmount !== ''
          ? parseFloat(String(values.sponsorAmount).replace('$', ''))
          : (isCoworking ? 15 : 0);

      const donorData: any = {
        name: values.name,
        email: values.email,
        phone: values.contact,
        coworking: isCoworking,
        sponsor_amount: sponsorAmount,
        sponsorAmount: sponsorAmount,
        invite_type: membership,
        inviteType: membership,
        // Both comped types settle outside Stripe; the app reads this to skip
        // the payment step during signup.
        external_billed: isCoworking || isTeam,
        externalBilled: isCoworking || isTeam,
        address: {
          city: values.cityState?.split(',')[0]?.trim() || '',
          state: values.cityState?.split(',')[1]?.trim() || ''
        },
        is_active: true,
        is_enabled: true
      };

      if (values.beneficiary) {
        donorData.beneficiary_id = Number(values.beneficiary);
      }
      
      const response = await donorAPI.createDonor(donorData);
      
      if (response.success) {
        message.success('Donor created successfully!');
        addNotification({
          title: 'Donor invited',
          message: `${values.name} (${values.email})`,
          level: 'success',
        });
        setIsInviteModalVisible(false);
        // Refresh the donors list
        loadDonors();
        return true;
      } else {
        const errMsg = response.error || response.message || 'Failed to create donor';
        message.error(errMsg);
        addNotification({
          title: 'Donor invite failed',
          message: errMsg,
          level: 'error',
        });
        return false;
      }
    } catch (error) {
      console.error('Error creating donor:', error);
      const errorMessage =
        error instanceof Error && error.message
          ? `Failed to create donor: ${error.message}`
          : 'Failed to create donor. Please try again.';
      message.error(errorMessage);
      addNotification({
        title: 'Donor invite failed',
        message: errorMessage,
        level: 'error',
      });
      return false;
    }
  };

  const handleEditDonor = (record: any) => {
    if (!record) {
      console.error('No record provided to handleEditDonor');
      message.error('Cannot edit: No donor selected');
      return;
    }
    
    if (!record.id && !record.key) {
      console.error('Record missing ID:', record);
      message.error('Cannot edit: Donor ID missing');
      return;
    }
    
    setEditingDonor(record);
    setIsEditModalVisible(true);
  };

  const openBilling = async (record: any) => {
    setBillingDonor(record);
    setBillingData(null);
    setBillingLoading(true);
    try {
      const { donorAPI } = await import('../services/api');
      const resp = await donorAPI.getDonorBilling(record.id);
      setBillingData((resp as any)?.data ?? null);
    } catch (error: any) {
      message.error(`Could not load billing: ${error?.message || 'unknown error'}`);
    } finally {
      setBillingLoading(false);
    }
  };

  const handleUpdateDonor = async (values: any) => {
    if (!editingDonor || !editingDonor.id) {
      message.error('Cannot update donor: missing donor ID');
      return;
    }

    try {
      setLoading(true);
      
      // Parse city and state from cityState
      const cityStateParts = values.cityState?.split(', ') || [values.city || '', values.state || ''];
      const city = cityStateParts[0] || values.city || '';
      const state = cityStateParts[1] || values.state || '';
      
      // Prepare donor data for API
      // Note: Fields like beneficiary_name, coworking, total_donations are typically calculated
      // from related tables and may be ignored by the backend during updates
      const donorData: any = {
        name: values.name,
        email: values.email,
        phone: values.contact,
        address: {
          city: city,
          state: state,
          zipCode: values.zipCode || '',
          street: '' // Street address not currently in form, but backend expects it
        },
        beneficiary_id: values.beneficiary ? Number(values.beneficiary) : undefined,
        donation_amount: values.donation !== undefined && values.donation !== null && values.donation !== ''
          ? parseFloat(String(values.donation).replace('$', ''))
          : undefined,
        one_time_donation: values.oneTime !== undefined && values.oneTime !== null && values.oneTime !== ''
          ? parseFloat(String(values.oneTime).replace('$', ''))
          : undefined,
        // Membership replaced the coworking yes/no. Both are sent: invite_type is
        // what the server keys off, and coworking keeps older rows consistent.
        invite_type: values.membership || 'standard',
        inviteType: values.membership || 'standard',
        coworking: values.membership === 'coworking',
        external_billed:
          values.membership === 'coworking' || values.membership === 'team',
        is_active: editingDonor.active !== undefined ? editingDonor.active : true,
        is_enabled: editingDonor.enabled !== undefined ? editingDonor.enabled : true
        // Note: 'notes' field removed - column doesn't exist in users table
        // Note: beneficiary_name, coworking, total_donations, one_time_donation, 
        // last_donation_date are typically calculated fields and may be ignored by backend
      };
      
      // Only include notes if the column exists (check with backend team first)
      // if (values.notes) {
      //   donorData.notes = values.notes;
      // }
      
      const response = await donorAPI.updateDonor(editingDonor.id, donorData);
      
      if (response.success || response.data) {
        message.success('Donor updated successfully!');
        setIsEditModalVisible(false);
        setEditingDonor(null);
        // Refresh the donors list
        await loadDonors();
      } else {
        message.error(response.message || 'Failed to update donor');
      }
    } catch (error: any) {
      console.error('Error updating donor:', error);
      console.error('Error details:', error.message);
      
      // Check error type and show appropriate message
      if (error.message && error.message.includes('404')) {
        message.error('Donor not found. Please verify the donor ID.');
      } else if (error.message && error.message.includes('500')) {
        message.error(`Server error: ${error.message}. Please check the backend logs.`);
      } else if (error.message && error.message.includes('400')) {
        message.error(`Invalid data: ${error.message}`);
      } else {
        message.error(error.message || 'Failed to update donor. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvitation = async (record: any) => {
    if (!record || !record.id) {
      message.error('Cannot resend invitation: missing donor ID');
      return;
    }

    setResendingInvitation(record.id);

    try {
      const response = await donorAPI.resendInvitation(record.id);
      
      if (response.success || response.data) {
        message.success(`Invitation email resent successfully to ${record.email || record.name}`);
        addNotification({
          title: 'Donor invite resent',
          message: record.email || record.name || 'Invitation resent',
          level: 'success',
        });
        // Optionally refresh the donor list
        await loadDonors();
      } else {
        const errorMsg = response.error || response.message || 'Failed to resend invitation email';
        message.error(errorMsg);
        addNotification({
          title: 'Resend invitation failed',
          message: errorMsg,
          level: 'error',
        });
      }
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      
      // Extract error message - prefer details if available as it's more specific
      let errorMessage = 'Failed to resend invitation. Please try again.';
      
      if (error.details) {
        // Use the parsed details which should contain the actual error from email service
        errorMessage = error.details;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      // Show more detailed error for 500 errors with extended duration
      if (error.status === 500) {
        errorMessage = errorMessage || 'Server error. Please contact support if this issue persists.';
        message.error({
          content: `❌ ${errorMessage}`,
          duration: 8, // Extended duration for detailed errors
        });
      } else {
        message.error(`❌ ${errorMessage}`);
      }
      addNotification({
        title: 'Resend invitation failed',
        message: errorMessage,
        level: 'error',
      });
    } finally {
      setResendingInvitation(null);
    }
  };

  const handleDeleteUser = (record: any) => {
    setDeletingUser(record);
    setIsDeleteUserModalVisible(true);
  };

  const confirmDeleteUser = async () => {
    if (!deletingUser || !deletingUser.id) {
      message.error('Cannot delete donor: missing donor ID');
      return;
    }

    try {
      setLoading(true);
      const response = await donorAPI.deleteDonor(deletingUser.id);
      const isSuccess = response?.error !== undefined ? false : (response?.success !== false || response?.data !== undefined);
      
      if (isSuccess) {
        message.success(`Donor ${deletingUser.name || deletingUser.email} deleted successfully`);
        addNotification({
          title: 'Donor deleted',
          message: deletingUser.email || deletingUser.name || 'Donor deleted',
          level: 'warning',
        });
        setIsDeleteUserModalVisible(false);
        setDeletingUser(null);
        await loadDonors();
      } else {
        message.error(response?.message || response?.error || 'Failed to delete donor');
      }
    } catch (error: any) {
      console.error('Error deleting donor:', error);
      message.error(error.message || 'Failed to delete donor. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout className="donors-layout">
      <>
        {/* Mobile Menu Button - Right Side */}
        <Button
          className="mobile-menu-btn-right"
          icon={<MenuOutlined />}
          onClick={() => setMobileSidebarVisible(!mobileSidebarVisible)}
        />

        {/* Sidebar */}
        <Sider
        width={280}
        className={`standard-sider ${mobileSidebarVisible ? 'mobile-visible' : ''}`}
        trigger={null}
      >
        <div className="standard-logo-section">
          <div className="standard-logo-container">
            <img
              src="/white-logo.png"
              alt="THRIVE Logo"
              className="standard-logo-image"
            />
          </div>
        </div>

        <Menu
          mode="inline"
          defaultSelectedKeys={['donors']}
          selectedKeys={[location.pathname === '/donors' ? 'donors' : '']}
          items={menuItems}
          className="standard-menu"
          onClick={handleMenuClick}
        />

        <UserProfile className="standard-user-profile" showRole={true} />
      </Sider>

      {/* Main Content */}
      <Layout className="standard-main-content">
        <Header className="donors-header">
          <div className="header-left">
            <Title level={2} style={{ margin: 0 }}>Donors</Title>
            <Text type="secondary" className="donors-count">
              {filteredDonorsData.length} Donor{filteredDonorsData.length !== 1 ? 's' : ''} Found
              {filteredDonorsData.length !== totalDonors && ` (of ${totalDonors} total)`}
            </Text>
          </div>
          <div className="header-right">
            <Button 
              type="primary" 
              icon={<UserAddOutlined />}
              size="large"
              className="invite-donor-btn"
              onClick={() => setIsInviteModalVisible(true)}
            >
              Invite A Donor
            </Button>
          </div>
        </Header>

        <Content className="donors-content">
          <div className="content-wrapper">
            {/* Search and Filter Bar */}
            <div className="search-filter-bar">
              <div className="search-section">
                <Search
                  placeholder="Search Donor"
                  allowClear
                  enterButton={<SearchOutlined />}
                  size="large"
                  className="donor-search"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={setSearchText}
                />
              </div>
              
              <div className="filter-section">
                <div className="filter-dropdowns">
                  <Text strong className="filter-label">Filters</Text>
                  <Select
                    placeholder="Select Beneficiary"
                    className="filter-dropdown"
                    size="large"
                    value={selectedBeneficiary}
                    onChange={setSelectedBeneficiary}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {beneficiariesList.map((beneficiary: any) => (
                      <Option key={beneficiary.id} value={beneficiary.id?.toString()}>
                        {beneficiary.name || 'Unknown'}
                      </Option>
                    ))}
                  </Select>
                  
                  <DatePicker
                    picker="month"
                    placeholder="Donated in month"
                    className="filter-dropdown"
                    size="large"
                    value={selectedMonth}
                    onChange={(d) => setSelectedMonth(d)}
                    format="MMMM YYYY"
                    // Nothing was collected before the platform launched, so
                    // earlier months can only ever return nothing.
                    disabledDate={(d) => d && d.isAfter(dayjs(), 'month')}
                    allowClear
                  />
                  
                  <Select
                    placeholder="All User"
                    className="filter-dropdown"
                    size="large"
                    value={selectedUserStatus}
                    onChange={setSelectedUserStatus}
                    allowClear
                  >
                    <Option value="all">All Users</Option>
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>

                  <Select
                    placeholder="Subscription"
                    className="filter-dropdown"
                    size="large"
                    value={selectedSubscriptionStatus}
                    onChange={setSelectedSubscriptionStatus}
                    allowClear
                  >
                    <Option value="active">Active</Option>
                    <Option value="past_due">Past Due</Option>
                    <Option value="cancelled">Cancelled</Option>
                    <Option value="no_subscription">No Subscription</Option>
                  </Select>
                  
                  <Select
                    placeholder="City, State"
                    className="filter-dropdown"
                    size="large"
                    value={selectedCityState}
                    onChange={setSelectedCityState}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {citiesList.map((cityState: string) => (
                      <Option key={cityState} value={cityState}>
                        {cityState}
                      </Option>
                    ))}
                  </Select>

                  <Select
                    placeholder="Membership"
                    className="filter-dropdown"
                    size="large"
                    value={selectedCoworking}
                    onChange={setSelectedCoworking}
                    allowClear
                  >
                    <Option value="standard">Standard</Option>
                    <Option value="coworking">Coworking</Option>
                    <Option value="team">Team</Option>
                  </Select>

                  <Button
                    size="large"
                    disabled={!searchText && !selectedBeneficiary && !selectedMonth && !selectedUserStatus && !selectedCityState && !selectedCoworking && !selectedSubscriptionStatus}
                    onClick={() => {
                      setSearchText('');
                      setSelectedBeneficiary(undefined);
                      setSelectedMonth(null);
                      setSelectedUserStatus(undefined);
                      setSelectedCityState(undefined);
                      setSelectedCoworking(undefined);
                      setSelectedSubscriptionStatus(undefined);
                    }}
                    style={{ color: '#DB8633', borderColor: '#DB8633' }}
                  >
                    Clear All
                  </Button>
                </div>
              </div>
              

            </div>

            {/* Donors Table */}
            <div className="donors-table-section">
              <Spin spinning={loading}>
                
                <Table
                  dataSource={paginatedDonors}
                  columns={columns}
                  pagination={false}
                  size="middle"
                  className="donors-table"
                  rowClassName="donor-row"
                  scroll={{ x: 2140 }}
                  bordered={false}
                  rowKey={(record) => record.key || record.id || 'unknown'}
                  virtual={false}
                  onChange={(_, __, sorter) => {
                    if (Array.isArray(sorter)) {
                      return;
                    }
                    const nextField = (sorter as any).field as string | undefined;
                    const nextOrder = (sorter as any).order as 'ascend' | 'descend' | null | undefined;
                    setSortField(nextField || null);
                    setSortOrder(nextOrder || null);
                  }}
                  onRow={(record, index) => {
                    return {
                      onClick: (event: React.MouseEvent) => {
                        // Check if clicking on buttons or other interactive elements
                        const target = event.target as HTMLElement;
                        if (!target) return;
                        
                        // Don't open modal if clicking on buttons, toggles, or action column
                        const isButton = target.closest('button') !== null;
                        const isToggle = target.closest('.toggle') !== null || target.closest('.toggle-switch') !== null;
                        const isAntBtn = target.closest('.ant-btn') !== null;
                        const isSpace = target.closest('.ant-space') !== null;
                        const isActionsColumn = target.closest('.ant-table-cell')?.querySelector('.ant-space') !== null;
                        
                        if (isButton || isToggle || isAntBtn || isSpace || isActionsColumn) {
                          return; // Let the button handle its own click
                        }
                        
                        // Otherwise, open edit modal
                        handleEditDonor(record);
                      },
                      style: { 
                        cursor: 'pointer',
                        userSelect: 'none'
                      }
                    };
                  }}
                  locale={{
                    emptyText: error
                      ? `Error: ${error}`
                      : 'No donors found. Use "Invite A Donor" button to add one.'
                  }}
                />
              </Spin>
              
              {/* Pagination, or a plain count when showing everything.
                  The toggle sits here rather than in the header so it reads as
                  an alternative to paging, which is what it is. */}
              <div
                className="pagination-section"
                style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}
              >
                {showAllDonors ? (
                  <Text type="secondary">
                    Showing all {filteredDonorsData.length} matching donor
                    {filteredDonorsData.length === 1 ? '' : 's'}
                  </Text>
                ) : (
                  <Pagination
                    current={currentPage}
                    total={filteredDonorsData.length}
                    pageSize={pageSize}
                    showSizeChanger={false}
                    showQuickJumper={false}
                    onChange={handlePageChange}
                    className="donors-pagination"
                  />
                )}
                {filteredDonorsData.length > pageSize && (
                  <Button
                    type="link"
                    onClick={() => {
                      // Back to page 1 when returning to paged view, or the
                      // pager could land on a page that no longer exists.
                      setCurrentPage(1);
                      setShowAllDonors((v) => !v);
                    }}
                    style={{ paddingLeft: 0 }}
                  >
                    {showAllDonors
                      ? 'Show pages'
                      : `View all ${filteredDonorsData.length}`}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Content>
      </Layout>
      
      {/* Invite Donor Modal */}
      {/* Billing, read live from Stripe.
          Deliberately Stripe-first: our monthly_donations table can disagree
          with what a donor is actually charged. It listed one $18.72/month
          subscription for ramon@workatthrive.com while Stripe was also billing
          $23.83/month on a customer never linked to his user row. Anything
          Stripe knows about and we don't is flagged rather than omitted. */}
      <Drawer
        title={
          billingDonor
            ? `Billing — ${billingDonor.name || billingDonor.email}`
            : 'Billing'
        }
        placement="right"
        width={Math.min(880, typeof window !== 'undefined' ? window.innerWidth - 80 : 880)}
        open={!!billingDonor}
        onClose={() => {
          setBillingDonor(null);
          setBillingData(null);
        }}
        destroyOnClose
      >
        <Spin spinning={billingLoading}>
          {!billingData && !billingLoading && (
            <Empty description="No billing data" />
          )}

          {billingData && (
            <>
              <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                <Col xs={12} md={8}>
                  <Card size="small">
                    <Statistic
                      title="Charged per month"
                      value={`$${(billingData.live_monthly_total_usd ?? 0).toFixed(2)}`}
                    />
                  </Card>
                </Col>
                <Col xs={12} md={8}>
                  <Card size="small">
                    <Statistic
                      title="Live subscriptions"
                      value={billingData.live_subscription_count ?? 0}
                      valueStyle={
                        (billingData.live_subscription_count ?? 0) > 1
                          ? { color: '#cf1322' }
                          : undefined
                      }
                    />
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card size="small">
                    <Statistic
                      title="Paid to date (Stripe)"
                      value={`$${(billingData.total_paid_usd ?? 0).toFixed(2)}`}
                    />
                  </Card>
                </Col>
              </Row>

              {(billingData.live_subscription_count ?? 0) > 1 && (
                <Card
                  size="small"
                  style={{ marginBottom: 16, borderColor: '#ffa39e', background: '#fff1f0' }}
                >
                  <Text strong style={{ color: '#cf1322' }}>
                    This donor has more than one live subscription and is being charged
                    multiple times a month.
                  </Text>
                </Card>
              )}

              {(billingData.unlinked_customers || []).length > 0 && (
                <Card
                  size="small"
                  style={{ marginBottom: 16, borderColor: '#ffd591', background: '#fffbe6' }}
                >
                  <Text>
                    <strong>Stripe customers not linked to this account:</strong>{' '}
                    {(billingData.unlinked_customers || []).join(', ')}. Charges on these
                    are real but were invisible to donor-based reports.
                  </Text>
                </Card>
              )}

              <Title level={5} style={{ marginTop: 8 }}>Subscriptions</Title>
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={billingData.all_stripe_subscriptions || []}
                locale={{ emptyText: 'No subscriptions on Stripe' }}
                columns={[
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    render: (v: string) => (
                      <Tag color={
                        ['active', 'trialing'].includes(v) ? 'green'
                          : ['past_due', 'unpaid', 'incomplete'].includes(v) ? 'orange'
                          : 'default'
                      }>{v}</Tag>
                    ),
                  },
                  {
                    title: 'Amount',
                    dataIndex: 'amount',
                    render: (v: number, r: any) =>
                      `$${((v || 0) / 100).toFixed(2)}${r.interval ? `/${r.interval}` : ''}`,
                  },
                  {
                    title: 'Started',
                    dataIndex: 'created',
                    render: (v: string) => (v ? v.slice(0, 10) : '—'),
                  },
                  {
                    title: 'Next bill',
                    dataIndex: 'current_period_end',
                    render: (v: string, r: any) =>
                      r.cancel_at_period_end ? 'cancels at period end' : v ? v.slice(0, 10) : '—',
                  },
                  {
                    title: 'In our DB',
                    dataIndex: 'known_locally',
                    render: (v: boolean) =>
                      v ? <Tag color="blue">tracked</Tag> : <Tag color="red">not tracked</Tag>,
                  },
                ]}
              />

              <Title level={5} style={{ marginTop: 20 }}>Invoice history</Title>
              <Table
                size="small"
                rowKey="id"
                pagination={{ pageSize: 10, size: 'small' }}
                dataSource={billingData.invoices || []}
                locale={{ emptyText: 'No invoices on Stripe' }}
                columns={[
                  {
                    title: 'Date',
                    dataIndex: 'created',
                    render: (v: string) => (v ? v.slice(0, 10) : '—'),
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    render: (v: string) => (
                      <Tag color={
                        v === 'paid' ? 'green'
                          : v === 'open' ? 'orange'
                          : v === 'void' ? 'default'
                          : 'red'
                      }>{v}</Tag>
                    ),
                  },
                  {
                    title: 'Paid',
                    dataIndex: 'amount_paid',
                    render: (v: number) => `$${((v || 0) / 100).toFixed(2)}`,
                  },
                  {
                    title: 'Due',
                    dataIndex: 'amount_due',
                    render: (v: number) => `$${((v || 0) / 100).toFixed(2)}`,
                  },
                  {
                    title: 'Invoice',
                    dataIndex: 'hosted_invoice_url',
                    render: (v: string, r: any) =>
                      v ? (
                        <a href={v} target="_blank" rel="noreferrer">open</a>
                      ) : (
                        <Text type="secondary" style={{ fontSize: 12 }}>{r.id}</Text>
                      ),
                  },
                ]}
              />

              {(billingData.saved_cards || []).length > 0 && (
                <>
                  <Title level={5} style={{ marginTop: 20 }}>Saved cards</Title>
                  {(billingData.saved_cards || []).map((c: any) => (
                    <Tag key={c.id} style={{ marginBottom: 6 }}>
                      {c.brand} ****{c.last4} · exp {c.exp}
                    </Tag>
                  ))}
                </>
              )}
            </>
          )}
        </Spin>
      </Drawer>

      <InviteDonorModal
        visible={isInviteModalVisible}
        onCancel={() => setIsInviteModalVisible(false)}
        onSubmit={handleInviteDonor}
        beneficiaries={beneficiariesList}
      />

      {/* Edit Donor Modal */}
      <EditDonorModal
        visible={isEditModalVisible}
        donor={editingDonor}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingDonor(null);
        }}
        onSubmit={handleUpdateDonor}
        beneficiaries={beneficiariesList}
      />

      {/* Delete User Confirmation Modal */}
      <Modal
        title="Delete Donor"
        open={isDeleteUserModalVisible}
        onOk={confirmDeleteUser}
        onCancel={() => {
          setIsDeleteUserModalVisible(false);
          setDeletingUser(null);
        }}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        confirmLoading={loading}
      >
        {deletingUser && (
          <div>
            <p>Are you sure you want to permanently delete this donor?</p>
            <div style={{ 
              padding: '16px', 
              background: '#fff7e6', 
              borderRadius: '4px',
              marginTop: '16px',
              marginBottom: '16px'
            }}>
              <Text strong>Donor Details:</Text>
              <br />
              <Text>Name: {deletingUser.name}</Text>
              <br />
              <Text>Email: {deletingUser.email}</Text>
              {deletingUser.beneficiary && (
                <>
                  <br />
                  <Text>Beneficiary: {deletingUser.beneficiary}</Text>
                </>
              )}
              {deletingUser.donation && (
                <>
                  <br />
                  <Text>Total Donations: {deletingUser.donation}</Text>
                </>
              )}
            </div>
            <p style={{ color: '#ff4d4f', marginBottom: 0 }}>
              <Text strong>Warning:</Text> This action cannot be undone. All donor data including donations, profile picture, and other associated information will be permanently deleted.
            </p>
          </div>
        )}
      </Modal>
      </>
    </Layout>
  );
};

export default Donors; 